/**
 * @file: useChatComposer.ts
 *
 * All state the composer OWNS — the live draft (text; typing heartbeats are
 * derived from draft changes by an RxJS pipeline, not hand-throttled), the
 * voice recorder, the attachment menu + card pickers, and the AI response
 * composer. It composes the keyboard inset and the `@mention` state, and
 * funnels every send through the shared pipeline on the room context
 * (`sendPayload` / `sendMediaAssets` / `sendFile` / `sendAudio`).
 *
 * It lives in `WiredComposer`, next to the composer it drives — so a
 * keystroke re-renders the composer subtree, never the whole conversation.
 */

import { useEntityCardPicker } from '@euler/app/components/entity-card-picker/useEntityCardPicker';
import { AttachmentAction } from '@euler/app/flows/chat/components/composer/AttachmentPanel';
import { MediaSendStyle } from '@euler/app/flows/chat/components/composer/MediaSendStylePanel';
import {
  captureChatMedia,
  pickChatFile,
  pickChatMedia,
} from '@euler/app/flows/chat/components/composer/attachmentActions';
import { buildAudioWaveform } from '@euler/app/flows/chat/utils/buildAudioWaveform';
import { taskCardBaseFromItem } from '@euler/app/flows/chat/utils/chatCardDescriptor';
import { isWithinEditWindow } from '@euler/app/flows/chat/utils/editWindow';
import { resolveTextMentions } from '@euler/app/flows/chat/utils/mentions';
import { ChatMediaUploadAsset } from '@euler/app/flows/chat/utils/uploadChatMedia';
import { useVoiceRecording } from '@euler/functions/useVoiceRecording';
import { useAlert } from '@euler/functions/ux/useAlert';
import {
  ChatContact,
  ChatUserMessagePayload,
  RemoteChatMsg,
} from '@euler/model/chat';
import { onErrorIgnore } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { BehaviorSubject, Subject, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  throttleTime,
} from 'rxjs/operators';
import { useChatConversation } from '../context';
import { useKeyboardInset } from './useKeyboardInset';
import { useMentionCompose } from './useMentionCompose';

/** Re-announce "typing" at most once per this window while the draft is live. */
const TYPING_HEARTBEAT_MS = 3000;

export function useChatComposer() {
  const {
    sendPayload,
    sendMediaAssets,
    sendMediaSeparately,
    sendFile,
    sendAudio,
    coordinator,
    messages$,
  } = useChatConversation();
  const alert = useAlert();
  // Typing is a coordinator concern: the composer just expresses the intent
  // (heartbeat / stopped); the coordinator broadcasts it once a room exists.
  // Edit mode is driven off the menu's `editRequested$`; a submitted edit goes
  // back to the store via `editSubmitted$`.
  const { typing$, endTyping$, editRequested$, editSubmitted$, quoteTarget$ } =
    coordinator;
  const { pickOrder, pickServiceRecord, pickReport } = useEntityCardPicker();

  const keyboard = useKeyboardInset();
  const mention = useMentionCompose(keyboard);
  const { mentions, setMentions } = mention;

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [aiComposerVisible, setAiComposerVisible] = useState(false);
  const [contactPickerVisible, setContactPickerVisible] = useState(false);
  // The message being edited (own plain-text, in-window). When set, the
  // composer is in edit mode: the input is prefilled and Send saves instead.
  const [editingMessage, setEditingMessage] = useState<RemoteChatMsg | null>(
    null,
  );
  // A multi-asset pick awaiting a send-style choice (null = chooser closed).
  // A single asset skips the chooser and sends as a `media` message directly.
  const [pendingMedia, setPendingMedia] = useState<
    ChatMediaUploadAsset[] | null
  >(null);

  // Every draft snapshot flows through here; the typing broadcast is derived
  // from this stream rather than hand-throttled at the keystroke callback.
  const textChange$ = useCreation(() => new Subject<string>(), []);
  const typingSignal$ = useCreation(
    () =>
      textChange$.pipe(
        // Collapse to "does the draft have content?", and react only when that
        // crosses the empty boundary — each crossing starts/ends a session.
        map(val => val.trim().length > 0),
        distinctUntilChanged(),
        switchMap(hasText =>
          hasText
            ? // A typing session: announce immediately on entry, then at most
              // once per heartbeat window for as long as the user keeps typing.
              // Re-subscribing here resets the throttle, so retyping after a
              // clear re-announces at once instead of waiting out the window.
              textChange$.pipe(
                filter(val => val.trim().length > 0),
                map(() => 'typing' as const),
                startWith('typing' as const),
                throttleTime(TYPING_HEARTBEAT_MS),
              )
            : of('stopped' as const),
        ),
      ),
    [textChange$],
  );

  useEffect(() => {
    const sub = typingSignal$.subscribe(signal =>
      signal === 'typing' ? typing$.next() : endTyping$.next(),
    );
    return () => sub.unsubscribe();
  }, [typingSignal$, typing$, endTyping$]);

  // Voice messages (the left mic button); dictation is the shared voice
  // composer's own. Live mic level streams through `voiceMeter$` so only the
  // waveform re-renders per tick, not the whole composer.
  const voiceMeter$ = useCreation(() => new BehaviorSubject(0), []);
  const waveformSamplesRef = useRef<number[]>([]);
  const voiceRecording = useVoiceRecording({
    onMeter: level => {
      voiceMeter$.next(level);
      waveformSamplesRef.current.push(level);
    },
  });
  const [voiceActive, setVoiceActive] = useState(false);
  const recordStartRef = useRef<number>(0);
  // Guards `completeVoice` against a double-fire (rapid tap / Android
  // back-gesture firing onComplete twice) — a second `stop()` would throw
  // or double-send.
  const voiceCompletingRef = useRef(false);

  // The single way to mutate the draft: keep `text` in sync, drop dangling
  // mention chips once it empties, and feed the typing pipeline. Both
  // keystrokes and the post-send clear go through here. Persisted (stable
  // identity, reads latest state) so the composer subtree's props don't churn
  // on every mention edit.
  const updateDraft = usePersistFn((val: string) => {
    setText(val);
    if (val.length === 0 && mentions.length > 0) setMentions([]);
    textChange$.next(val);
  });

  // Enter edit mode for an own plain-text message: prefill the draft, drop any
  // active reply target (edit and reply are mutually exclusive), and focus.
  const enterEditMode = usePersistFn((message: RemoteChatMsg) => {
    if (message.payload.type !== 'text') return;
    quoteTarget$.next(null);
    setEditingMessage(message);
    updateDraft(message.payload.text);
    mention.composerRef.current?.focus();
  });

  const cancelEdit = usePersistFn(() => {
    setEditingMessage(null);
    updateDraft('');
  });

  useEffect(() => {
    const sub = editRequested$.subscribe(message => {
      if (message.kind === 'remote') enterEditMode(message);
    });
    return () => sub.unsubscribe();
  }, [editRequested$, enterEditMode]);

  // Tear down edit mode if the target stops being editable underneath us — a
  // remote delete/edit landed, or the list was re-seeded (resync / remount) and
  // the message is gone. Prevents submitting an edit against a stale snapshot.
  useEffect(() => {
    if (!editingMessage) return;
    const id = editingMessage.messageId;
    const sub = messages$.subscribe(list => {
      const m = list.find(it => it.kind === 'remote' && it.messageId === id);
      if (!m || (m.kind === 'remote' && m.deletedAt)) cancelEdit();
    });
    return () => sub.unsubscribe();
  }, [editingMessage, messages$, cancelEdit]);

  const sendMessage = usePersistFn(async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    // Resolve mention ranges against the FINAL text (trimming shifts
    // offsets), dropping any chip whose `@<name>` token was edited away.
    const resolved = resolveTextMentions(trimmed, mentions);
    const payload: ChatUserMessagePayload = { type: 'text', text: trimmed };
    if (resolved.length > 0) payload.mentions = resolved;
    // In edit mode, Send SAVES the edit (back to the store via the bus) rather
    // than posting a new message; clear the draft + exit edit mode at once.
    if (editingMessage) {
      // Re-check the window at submit — the user may have dwelled in the
      // composer past it (the server would reject; surface it instead of a
      // silent revert).
      if (!isWithinEditWindow(editingMessage.createdAt)) {
        cancelEdit();
        void alert({ title: '无法编辑', message: '超过可编辑时间' });
        return;
      }
      const messageId = editingMessage.messageId;
      setEditingMessage(null);
      updateDraft('');
      editSubmitted$.next({ messageId, payload });
      return;
    }
    // Clear the input the instant send is pressed — the bubble is inserted
    // optimistically, and a failed send stays recoverable via the in-bubble
    // retry, so there's no need to keep the draft around. Emptying the draft
    // also lets the typing pipeline read it as "stopped" + a session reset, so
    // the next keystroke re-announces immediately.
    updateDraft('');
    setSending(true);
    try {
      await sendPayload(payload, { quote: true });
    } catch (e) {
      console.error('send failed', e);
    } finally {
      setSending(false);
    }
  });

  const pickAndSendMedia = useCallback(
    async (capture: boolean) => {
      try {
        const assets = capture
          ? await captureChatMedia()
          : await pickChatMedia();
        if (assets.length === 0) return;
        // One asset sends straight as `media`; a multi-pick asks how to send.
        if (assets.length === 1) await sendMediaAssets(assets);
        else setPendingMedia(assets);
      } catch (e) {
        console.error('pick media failed', e);
      }
    },
    [sendMediaAssets],
  );

  // Resolve the multi-pick chooser: send one-by-one or as one laid-out album.
  const confirmMediaStyle = useCallback(
    (style: MediaSendStyle) => {
      const assets = pendingMedia;
      setPendingMedia(null);
      if (!assets) return;
      if (style.mode === 'separate') void sendMediaSeparately(assets);
      else void sendMediaAssets(assets, { layout: style.layout });
    },
    [pendingMedia, sendMediaSeparately, sendMediaAssets],
  );

  const closeMediaStylePanel = useCallback(() => setPendingMedia(null), []);

  // Paste-to-send images (web): catch a clipboard paste carrying image
  // file(s) and send them like a normal media pick.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onPaste = (e: ClipboardEvent) => {
      const files = e.clipboardData?.files;
      if (!files || files.length === 0) return;
      const images = Array.from(files).filter(f => f.type.startsWith('image/'));
      if (images.length === 0) return;
      e.preventDefault();
      const assets: ChatMediaUploadAsset[] = images.map(f => ({
        kind: 'image',
        uri: URL.createObjectURL(f),
        mimeType: f.type || 'image/png',
      }));
      if (assets.length === 1) void sendMediaAssets(assets);
      else setPendingMedia(assets);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [sendMediaAssets]);

  const pickAndSendFile = useCallback(async () => {
    try {
      const file = await pickChatFile();
      if (!file) return;
      await sendFile(file);
      // Release the object URL minted in pickChatFile so the blob isn't
      // pinned for the tab's lifetime (the bubble holds the uploaded URL
      // after reconciliation).
      if (file.uri.startsWith('blob:')) URL.revokeObjectURL(file.uri);
    } catch (e) {
      console.error('chat file upload failed', e);
    }
  }, [sendFile]);

  // Voice messages work on web too: the media service transcodes the
  // browser's webm.
  const startVoice = useCallback(async () => {
    try {
      voiceMeter$.next(0);
      waveformSamplesRef.current = [];
      await voiceRecording.start();
      // Stamp AFTER start() resolves so recorder/permission setup latency
      // doesn't inflate the measured duration.
      recordStartRef.current = Date.now();
      setVoiceActive(true);
    } catch (e) {
      console.error('voice recording start failed', e);
    }
  }, [voiceRecording, voiceMeter$]);

  const cancelVoice = useCallback(() => {
    setVoiceActive(false);
    voiceMeter$.next(0);
    voiceRecording.cancel().catch(onErrorIgnore);
  }, [voiceRecording, voiceMeter$]);

  const completeVoice = useCallback(async () => {
    if (voiceCompletingRef.current) return;
    voiceCompletingRef.current = true;
    setVoiceActive(false);
    voiceMeter$.next(0);
    try {
      let recorded: { uri: string; format: 'wav' | 'aac' } | null = null;
      try {
        recorded = await voiceRecording.stop();
      } catch (e) {
        console.error('voice recording stop failed', e);
        return;
      }
      const durationMs = Math.max(0, Date.now() - recordStartRef.current);
      const mimeType =
        Platform.OS === 'web'
          ? 'audio/webm'
          : Platform.OS === 'ios'
          ? 'audio/wav'
          : 'audio/aac';
      const waveform = buildAudioWaveform(waveformSamplesRef.current);
      await sendAudio({ uri: recorded.uri, mimeType, durationMs, waveform });
    } finally {
      voiceCompletingRef.current = false;
    }
  }, [voiceRecording, voiceMeter$, sendAudio]);

  const sendAIResponse = useCallback(
    (markdown: string) => {
      setAiComposerVisible(false);
      if (markdown.trim()) {
        void sendPayload({ type: 'markdown', markdown: markdown.trim() });
      }
    },
    [sendPayload],
  );

  const pickAndSendOrderCard = useCallback(async () => {
    const r = await pickOrder();
    if (!r || r.kind !== 'order') return;
    await sendPayload({ type: 'order_card', ...taskCardBaseFromItem(r.item) });
  }, [pickOrder, sendPayload]);

  const pickAndSendServiceRecordCard = useCallback(async () => {
    const r = await pickServiceRecord();
    if (!r || r.kind !== 'service-record') return;
    const t = r.item;
    const serviceRecordId =
      t.type === 'service-record' ? t.serviceRecordId : t.id;
    await sendPayload({
      type: 'service_record_card',
      ...taskCardBaseFromItem(t),
      serviceRecordId,
    });
  }, [pickServiceRecord, sendPayload]);

  const pickAndSendReportCard = useCallback(async () => {
    const r = await pickReport();
    if (!r || r.kind !== 'report') return;
    const t = r.item;
    await sendPayload({
      type: 'report_card',
      ...taskCardBaseFromItem(t),
      reportNo: t.reportNo,
      reportType: r.reportType,
    });
  }, [pickReport, sendPayload]);

  const closeAIComposer = useCallback(() => setAiComposerVisible(false), []);

  const closeContactPicker = useCallback(
    () => setContactPickerVisible(false),
    [],
  );

  /** Share a colleague's card — the picked contact carries every field. */
  const sendContactCard = useCallback(
    (contact: ChatContact) => {
      setContactPickerVisible(false);
      void sendPayload({
        type: 'contact_card',
        platformUserId: contact.platformUserId,
        appId: contact.appId,
        appScope: contact.appScope,
        name: contact.name,
        avatar: contact.avatar ?? undefined,
        title: contact.jobTitle ?? undefined,
        phone: contact.mobile ?? undefined,
      });
    },
    [sendPayload],
  );

  const onPressVoice = useCallback(() => void startVoice(), [startVoice]);

  // Words held and let go of on 发送 in the voice composer (or sent from its
  // editor): a text message of their own, the draft untouched. Editing a
  // message, or while a send is still going, they join the draft instead,
  // to be sent (or saved) from there.
  const sendSpokenText = usePersistFn(async (words: string) => {
    const trimmed = words.trim();
    if (!trimmed) return;
    if (editingMessage || sending) {
      updateDraft(text + trimmed);
      return;
    }
    setSending(true);
    try {
      await sendPayload({ type: 'text', text: trimmed }, { quote: true });
    } catch (e) {
      console.error('send failed', e);
    } finally {
      setSending(false);
    }
  });

  const handleSelectAttachment = useCallback(
    (action: AttachmentAction) => {
      switch (action) {
        case 'media':
          void pickAndSendMedia(false);
          break;
        case 'camera':
          void pickAndSendMedia(true);
          break;
        case 'file':
          void pickAndSendFile();
          break;
        case 'ai':
          setAiComposerVisible(true);
          break;
        case 'order':
          void pickAndSendOrderCard();
          break;
        case 'service_record':
          void pickAndSendServiceRecordCard();
          break;
        case 'report':
          void pickAndSendReportCard();
          break;
        case 'contact':
          setContactPickerVisible(true);
          break;
      }
    },
    [
      pickAndSendMedia,
      pickAndSendFile,
      pickAndSendOrderCard,
      pickAndSendServiceRecordCard,
      pickAndSendReportCard,
    ],
  );

  return {
    // keyboard inset (the composer strip pads its bottom by this)
    keyboardInset: keyboard.inset,
    // ChatComposer
    composerRef: mention.composerRef,
    text,
    onChangeText: updateDraft,
    sendMessage,
    sending,
    // edit mode
    editingMessage,
    cancelEdit,
    mentions,
    onMentionTrigger: mention.openMentionPicker,
    onSelectAttachment: handleSelectAttachment,
    // media send-style chooser (multi-pick)
    mediaStyleVisible: pendingMedia != null,
    mediaStyleAssetCount: pendingMedia?.length ?? 0,
    onConfirmMediaStyle: confirmMediaStyle,
    onCloseMediaStylePanel: closeMediaStylePanel,
    // voice
    voiceActive,
    voiceMeter$,
    onPressVoice,
    sendSpokenText,
    onCancelVoice: cancelVoice,
    onCompleteVoice: completeVoice,
    // mention picker
    mentionPickerOpen: mention.mentionPickerOpen,
    dismissMentionPicker: mention.dismissMentionPicker,
    onPickMention: mention.onPickMention,
    // AI composer
    aiComposerVisible,
    closeAIComposer,
    sendAIResponse,
    // contact-card picker
    contactPickerVisible,
    closeContactPicker,
    sendContactCard,
  };
}
